import * as React from "react";

import { cn } from "@/lib/utils";

const Input = React.forwardRef<HTMLInputElement, React.ComponentProps<"input">>(
  ({ className, type, onFocus, onWheel, ...props }, ref) => {
    const handleFocus = React.useCallback(
      (e: React.FocusEvent<HTMLInputElement>) => {
        // For numeric inputs, select the contents so typing replaces "0"
        // instead of forcing the user to backspace first.
        if (type === "number") {
          // Defer to next tick so iOS/Safari honor the selection.
          requestAnimationFrame(() => {
            try {
              e.target.select();
            } catch {
              /* noop */
            }
          });
        }
        onFocus?.(e);
      },
      [type, onFocus],
    );

    const handleWheel = React.useCallback(
      (e: React.WheelEvent<HTMLInputElement>) => {
        // Prevent accidental value changes from scroll while focused on number inputs.
        if (type === "number" && document.activeElement === e.currentTarget) {
          e.currentTarget.blur();
        }
        onWheel?.(e);
      },
      [type, onWheel],
    );

    return (
      <input
        type={type}
        onFocus={handleFocus}
        onWheel={handleWheel}
        className={cn(
          "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
          className,
        )}
        ref={ref}
        {...props}
      />
    );
  },
);
Input.displayName = "Input";

export { Input };
